/**
 * Frontend helpers for the carrier x capability x method matrix.
 *
 * The matrix is hardcoded on the backend (bookc-backend/clinics/carrier_capabilities.py)
 * and served via GET /clinics/carrier-capabilities/. controller.getCarrierCapabilities()
 * caches it at module level.
 *
 * A carrier is identified here by its InsuranceProvider.code (e.g. "SUNLIFE"),
 * which comes through on policies as `insurance_name` (see InsurancePolicyLiteSerializer).
 */

/** Canonical uppercase form for carrier codes. */
function normalizeCode(code) {
  return (code || "").toString().trim().toUpperCase();
}

/**
 * Get the list of methods supporting `capability` for `carrierCode`.
 * Falls back to the matrix `default` entry for unknown carriers.
 */
export function getMethods(matrix, carrierCode, capability) {
  if (!matrix || !capability) return [];
  const code = normalizeCode(carrierCode);
  const carriers = matrix.carriers || {};
  const fallback = matrix.default || {};
  const entry = carriers[code] || fallback;
  return Array.isArray(entry[capability]) ? entry[capability] : [];
}

/**
 * Does `carrierCode` support `capability` via ANY of the given `methods`?
 */
export function supportsAny(matrix, carrierCode, capability, methods) {
  if (!Array.isArray(methods) || methods.length === 0) return false;
  const supported = new Set(getMethods(matrix, carrierCode, capability));
  return methods.some((m) => supported.has(m));
}

/**
 * Pick the first AI method (in the matrix's `ai_method_preference` order)
 * that's supported for `capability` on `carrierCode`. Returns null otherwise.
 */
export function pickAIMethod(matrix, carrierCode, capability) {
  if (!matrix) return null;
  const supported = new Set(getMethods(matrix, carrierCode, capability));
  const pref = Array.isArray(matrix.ai_method_preference)
    ? matrix.ai_method_preference
    : ["scraper", "llm_navigator_scraper", "ai_phone_call"];
  for (const m of pref) {
    if (supported.has(m)) return m;
  }
  return null;
}

/** The three "AI fetch" method names — used by button-gating checks. */
export const AI_FETCH_METHODS = ["scraper", "llm_navigator_scraper", "ai_phone_call"];

/** Predetermination only uses scraper / llm_navigator_scraper (no phone). */
export const PREDETERMINATION_AI_METHODS = ["scraper", "llm_navigator_scraper"];

/**
 * Mirror the backend's portal_code_for_carrier: which portal handles a carrier,
 * so callers can pick the right set of provider accounts to fetch an estimate
 * under. Returns the portal_code, or null when the carrier isn't portal-backed.
 */
export function portalForCarrier(code) {
  const c = (code || "").toString().toUpperCase().trim();
  if (c === "SUNLIFE" || c === "CDCP") return "SUNLIFE";
  if (["GSC", "CCQ", "RBC", "SSQ"].indexOf(c) !== -1) return "PROVIDER_AGGREGATOR";
  return null;
}

/**
 * Extract the carrier code from a policy object. Policies surface the code
 * under different keys across endpoints; this normalizes access.
 */
export function carrierCodeFromPolicy(policy) {
  if (!policy) return "";
  return (
    policy.insurance_name ||
    policy.carrier_code ||
    policy.insurance_code ||
    ""
  );
}
